import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Trophy, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getMedalStyle } from '@/lib/medals';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';
import { useAuth } from '@/context/AuthContext';
import { GET_LIVE_LEADERBOARD } from '@/graphql/queries';

const POLL_INTERVAL = 3 * 60 * 1000;

export function LiveLeaderboardPage() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation('races');
    const { t: tc } = useTranslation();
    const { user: currentUser } = useAuth();

    const { data, loading } = useQuery(GET_LIVE_LEADERBOARD, {
        variables: { input: { raceId: id! } },
        skip: !id,
        pollInterval: POLL_INTERVAL,
    });

    const entries = data?.liveLeaderboard?.entries ?? [];
    const scoredRunners = entries[0]?.scoredRunners ?? 0;
    const totalRunners = entries[0]?.totalRunners ?? 0;

    if (!id) return null;

    return (
        <Page>
            <PageHeader
                title={t('details.liveTracking.leaderboard.pageTitle')}
                backLink={`/races/${id}`}
                width="standard"
            />

            <PageContent width="standard" className="space-y-3">
                {loading && (
                    <div className="flex items-center justify-center py-20 text-slate-400">
                        <Loader2 size={28} className="animate-spin text-emerald-500" />
                    </div>
                )}

                {!loading && entries.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                        <Trophy size={36} className="mb-3 opacity-20" />
                        <p className="text-sm font-medium">{tc('error.generic')}</p>
                    </div>
                )}

                {entries.length > 0 && (
                    <>
                        {scoredRunners > 0 && (
                            <p className="text-[11px] text-emerald-400 font-medium text-center">
                                {t('details.liveTracking.leaderboard.estimated', { scored: scoredRunners, total: totalRunners })}
                            </p>
                        )}

                        <div className="space-y-2">
                            {entries.map((entry, index) => {
                                const rank = index + 1;
                                const isMe = entry.user?.id === currentUser?.id;

                                return (
                                    <div
                                        key={entry.user?.id ?? index}
                                        className={cn(
                                            "flex items-center gap-3 py-3 px-4 rounded-2xl border transition-all",
                                            isMe
                                                ? "bg-emerald-50/80 border-emerald-200 ring-1 ring-emerald-100"
                                                : "bg-white border-slate-100"
                                        )}
                                    >
                                        <div className={cn(
                                            "w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0",
                                            getMedalStyle(rank)
                                        )}>
                                            {rank}
                                        </div>
                                        <span className={cn(
                                            "flex-1 text-sm font-medium truncate",
                                            isMe ? "text-emerald-900 font-bold" : "text-slate-700"
                                        )}>
                                            {entry.user?.username ?? '?'}
                                            {isMe && (
                                                <span className="ml-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                                                    {t('details.liveTracking.leaderboard.you')}
                                                </span>
                                            )}
                                        </span>
                                        <span className={cn(
                                            "text-lg font-black flex-shrink-0",
                                            isMe ? "text-emerald-600" : "text-slate-800"
                                        )}>
                                            {(entry.totalScore ?? 0).toFixed(1)}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-medium flex-shrink-0">
                                            {t('details.liveTracking.leaderboard.pts')}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </PageContent>
        </Page>
    );
}
